<template>
  <div class="tass-student-management-config">
    <v-row no-gutters>
      <v-col cols="12">
        <v-checkbox
          id="tickbox_studentDetailsApiGroupOnlyEnabled"
          v-model="form.studentDetailsApiGroupOnlyEnabled"
          label="Enable Student Details API Group Only"
          :disabled="serviceEnded"
          color="primary"
          @update:model-value="emitConfig"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>API Endpoint</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_apiEndpoint"
          v-model="form.apiEndpoint"
          variant="solo"
          density="compact"
          placeholder="API Endpoint"
          :error-messages="errors.apiEndpoint"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('apiEndpoint', 'API Endpoint')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>App Code</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_appCode"
          v-model="form.appCode"
          variant="solo"
          density="compact"
          placeholder="App Code"
          :error-messages="errors.appCode"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('appCode', 'App Code')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>Company Code</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_companyCode"
          v-model="form.companyCode"
          variant="solo"
          density="compact"
          placeholder="Company Code"
          :error-messages="errors.companyCode"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('companyCode', 'Company Code')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>Token Key</span>
        <span class="text-red">*</span>
      </v-col>
      <v-col cols="12">
        <v-text-field
          id="txt_tokenKey"
          v-model="form.tokenKey"
          variant="solo"
          density="compact"
          placeholder="Token Key"
          :error-messages="errors.tokenKey"
          :disabled="serviceEnded"
          @update:model-value="onTextInput('tokenKey', 'Token Key')"
        />
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>API Auth List</span>
      </v-col>
      <v-col cols="12">
        <v-textarea
          id="txt_apiAuthList"
          v-model="form.apiAuthList"
          variant="solo"
          density="compact"
          placeholder="API Auth List"
          :error-messages="errors.apiAuthList"
          :disabled="serviceEnded"
          @update:model-value="onJsonInput('apiAuthList', 'API Auth List')"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <span>Client-to-STB Medical Condition Map</span>
      </v-col>
      <v-col cols="12">
        <v-textarea
          id="txt_clientToStbMedicalConditionMap"
          v-model="form.clientToStbMedicalConditionMap"
          variant="solo"
          density="compact"
          placeholder="Client-to-STB Medical Condition Map"
          :error-messages="errors.clientToStbMedicalConditionMap"
          :disabled="serviceEnded"
          @update:model-value="
            onJsonInput('clientToStbMedicalConditionMap', 'Client-to-STB Medical Condition Map')
          "
        ></v-textarea>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { cloneDeep, has } from 'lodash';
const REQUIRED_TEXT_FIELDS = ['apiEndpoint', 'appCode', 'companyCode', 'tokenKey'];

export default {
  name: 'TassStudentManagementConfig',

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    serviceEnded: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:config', 'update:error-hash', 'update:valid'],

  data() {
    return {
      form: {
        apiEndpoint: '',
        appCode: '',
        companyCode: '',
        tokenKey: '',
        apiAuthList: '',
        clientToStbMedicalConditionMap: '',
        studentDetailsApiGroupOnlyEnabled: false
      },
      errors: {
        apiEndpoint: [],
        appCode: [],
        companyCode: [],
        tokenKey: [],
        apiAuthList: [],
        clientToStbMedicalConditionMap: []
      },
      errorHash: {}
    };
  },

  watch: {
    config: {
      immediate: true,
      handler() {
        this.applyConfig();
      }
    }
  },

  methods: {
    applyConfig() {
      const config = this.config || {};
      Object.keys(this.form).forEach((name) => {
        if (typeof config[name] === 'undefined') {
          return;
        }
        const value = config[name];
        const isJsonField = name === 'apiAuthList' || name === 'clientToStbMedicalConditionMap';
        this.form[name] =
          isJsonField && value && typeof value === 'object'
            ? JSON.stringify(value, null, 2)
            : value;
      });

      this.$emit('update:valid', this.isValid());
    },

    onTextInput(name, label) {
      this.errors[name] =
        !this.form[name] || `${this.form[name]}`.length === 0 ? [`${label} is required`] : [];
      this.emitConfig();
    },

    onJsonInput(name, label) {
      const value = this.form[name];
      const fieldErrors = [];

      if (typeof value !== 'undefined' && `${value}` !== '') {
        let parsedJson;
        try {
          parsedJson = JSON.parse(value);
        } catch (_err) {
          fieldErrors.push(`${label} needs to be a valid JSON`);
        }
        if (parsedJson && Object.keys(parsedJson).length <= 0) {
          fieldErrors.push(`${label} needs to be a non-empty JSON`);
        }
      }

      if (fieldErrors.length > 0) {
        this.errorHash = { ...this.errorHash, [name]: true };
      } else {
        const nextHash = { ...this.errorHash };
        delete nextHash[name];
        this.errorHash = nextHash;
      }

      this.errors[name] = fieldErrors;
      this.emitConfig();
    },

    emitConfig() {
      const config = cloneDeep(this.form);

      if (has(config, 'apiAuthList') && !this.errorHash.apiAuthList) {
        config.apiAuthList = config.apiAuthList === '' ? null : JSON.parse(config.apiAuthList);
      }

      if (
        has(config, 'clientToStbMedicalConditionMap') &&
        !this.errorHash.clientToStbMedicalConditionMap
      ) {
        config.clientToStbMedicalConditionMap =
          config.clientToStbMedicalConditionMap === ''
            ? null
            : JSON.parse(config.clientToStbMedicalConditionMap);
      }

      this.$emit('update:config', config);
      this.$emit('update:error-hash', this.errorHash);
      this.$emit('update:valid', this.isValid());
    },

    isValid() {
      const textValid = REQUIRED_TEXT_FIELDS.every(
        (name) => this.form[name] && `${this.form[name].trim()}`.length > 0
      );
      return textValid && Object.keys(this.errorHash).length === 0;
    }
  }
};
</script>
